import { AppBar, Divider, IconButton, ListItemText, Menu, MenuItem, Stack, Toolbar } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useContext, useEffect, useMemo, useState } from 'react'
import { com, Nullable } from '@eidu/form'
import MenuIcon from '@mui/icons-material/Menu'
import AuthenticationContext from '../../api/authorization/AuthenticationContext'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import useEntityRepository from '../../io/useEntityRepository'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { useSettings } from '../../io/context/Settings'
import { useAssetTypeRegistry } from '../../pages/assets/useAssetTypeRegistry'
import { findAssetTypeByLabel } from '../../pages/assets/assetTypes'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import useEntityById from '../../io/useEntityById'
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull
import { useNavigate } from 'react-router-dom'

const DEFAULT_LOGO_SRC = '/logo448.png'

type ParsedLogoSetting = {
  assetId?: EntityId
  url?: string
}

const parseLogoSetting = (value?: string): ParsedLogoSetting => {
  const trimmed = value?.trim()
  if (!trimmed) return {}
  if (trimmed.startsWith('asset://')) {
    const assetId = entityIdFromStringOrNull(trimmed.slice('asset://'.length))
    return assetId ? { assetId } : {}
  }
  const assetId = entityIdFromStringOrNull(trimmed)
  if (assetId) return { assetId }
  return { url: trimmed }
}

const getFieldString = (values: ReadonlyMap<FieldId, Nullable<FieldValue>>, fieldId?: FieldId) =>
  fieldId ? fieldValueToString(values.get(fieldId) ?? null).trim() : ''

const getAssetTypeLabel = (asset: EntityWithLabelAndRelated, typeFieldId?: com.eidu.sharedlib.entity.field.FieldId) => {
  if (!typeFieldId) return undefined
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const typeValue = values.get(typeFieldId) ?? null
  if (typeValue instanceof ReferenceValue) {
    const referenced = asset.getReferencedEntity(typeValue.value)
    return referenced?.primaryLabelText?.trim()
  }
  return typeValue ? fieldValueToString(typeValue).trim() : undefined
}

const resolveAssetTypeForEntity = (
  asset: EntityWithLabelAndRelated,
  assetConfig?: ReturnType<typeof useAssetTypeRegistry>['assetEntityTypes'][number]
) => {
  if (!assetConfig) return undefined
  if (assetConfig.allowedTypes.length === 1 && !assetConfig.typeFieldId) return assetConfig.allowedTypes[0]
  const typeLabel = getAssetTypeLabel(asset, assetConfig.typeFieldId)
  if (!typeLabel) return assetConfig.allowedTypes.length === 1 ? assetConfig.allowedTypes[0] : undefined
  const resolved = findAssetTypeByLabel(typeLabel)
  if (!resolved) return undefined
  return assetConfig.allowedTypeIds.includes(resolved.id) ? resolved : undefined
}

const resolveLogoSrcFromAsset = (
  asset: EntityWithLabelAndRelated,
  assetConfig?: ReturnType<typeof useAssetTypeRegistry>['assetEntityTypes'][number]
) => {
  if (!assetConfig) return undefined
  const assetType = resolveAssetTypeForEntity(asset, assetConfig)
  if (!assetType || assetType.mediaType !== 'image' || !assetType.mimeType) return undefined
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const payload = getFieldString(values, assetConfig.payloadFieldId)
  if (payload) return `data:${assetType.mimeType};base64,${payload}`
  const thumbnail = getFieldString(values, assetConfig.thumbnailFieldId)
  return thumbnail ? `data:image/png;base64,${thumbnail}` : undefined
}

export type TopAppBarProps = {
  authContext: AuthenticationContext | undefined
  signOut?: () => void
  showNavigationMenuButton?: boolean
  onNavigationMenuClick?: () => void
}

const TopAppBar = ({
  authContext,
  signOut,
  showNavigationMenuButton = false,
  onNavigationMenuClick,
}: TopAppBarProps) => {
  const navigate = useNavigate()
  const entityTypes = useContext(EntityTypesContext).useAll()
  const { getSettingValue } = useSettings()
  const logoSetting = getSettingValue('Organization Logo')
  const parsedLogo = useMemo(() => parseLogoSetting(logoSetting), [logoSetting])
  const { entity: logoAsset } = useEntityById(parsedLogo.assetId)
  const { assetEntityTypesById } = useAssetTypeRegistry(entityTypes ?? undefined)
  const entityRepository = useEntityRepository(
    { typeId: undefined, types: entityTypes ?? new Map<EntityTypeId, EntityType>() },
    [authContext, entityTypes]
  )

  const [userEntity, setUserEntity] = useState<EntityWithLabelAndRelated>()
  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (authContext)
      entityRepository.get(entityIdFromString(authContext?.userEntityId)).then((entity) => {
        if (entity !== null) setUserEntity(entity)
      })
  }, [authContext, entityRepository])

  const logoSrc = useMemo(() => {
    if (parsedLogo.url) return parsedLogo.url
    if (!logoAsset) return undefined
    const assetConfig = assetEntityTypesById.get(logoAsset.entity.typeId)
    return resolveLogoSrcFromAsset(logoAsset, assetConfig)
  }, [assetEntityTypesById, logoAsset, parsedLogo.url])

  const closeActionsMenu = () => {
    setActionsAnchor(null)
  }

  const openActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchor(event.currentTarget)
  }

  const openCurrentUser = () => {
    if (!userEntity) return
    closeActionsMenu()
    navigate(`/entities/${userEntity.entity.typeId.toString()}/${userEntity.entity.id.toString()}`)
  }

  const hasActionsMenu = Boolean(userEntity) || Boolean(signOut)
  const actionsMenu = hasActionsMenu ? (
    <>
      <IconButton edge="end" color="inherit" aria-label="Open user actions menu" onClick={openActionsMenu}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={closeActionsMenu}>
        {userEntity && (
          <MenuItem onClick={openCurrentUser}>
            <ListItemText
              primary="Your Profile"
              secondary={userEntity.primaryLabelText || userEntity.entity.id.toString()}
            />
          </MenuItem>
        )}
        {userEntity && signOut && <Divider />}
        {signOut && (
          <MenuItem
            onClick={() => {
              closeActionsMenu()
              signOut()
            }}
          >
            Log out
          </MenuItem>
        )}
      </Menu>
    </>
  ) : null

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        {showNavigationMenuButton ? (
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', justifySelf: 'start' }}>
              <IconButton
                color="inherit"
                edge="start"
                aria-label="Open navigation menu"
                onClick={onNavigationMenuClick}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
            <img src={logoSrc ?? DEFAULT_LOGO_SRC} className="appBarIcon" alt="" />
            <Stack direction="row" sx={{ alignItems: 'center', justifySelf: 'end' }}>
              {actionsMenu}
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'left' }}>
              <img src={logoSrc ?? DEFAULT_LOGO_SRC} className="appBarIcon" alt="" />
            </Stack>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'right' }}>
              {actionsMenu}
            </Stack>
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  )
}

export default TopAppBar
